import styled from 'styled-components'
import { Card, Button, Table, Nav } from 'react-bootstrap'
// Narrow React-Bootstrap Button's very large union props to simplify TS types in styled-components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBase = Button as unknown as React.ComponentType<any>

// Base card styling matching app theme
export const AdminCard = styled(Card)`
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.85) 0%, rgba(20, 20, 20, 0.9) 100%);
  border: 1px solid rgba(34, 211, 238, 0.18);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    border-color: rgba(34, 211, 238, 0.35);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 211, 238, 0.2),
      0 0 20px rgba(34, 211, 238, 0.15);
    transform: translateY(-4px);
  }

  .card-header {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.12) 0%, rgba(20, 184, 166, 0.08) 100%);
    border-bottom: 1px solid rgba(34, 211, 238, 0.18);
    padding: 1.25rem 1.5rem;
    backdrop-filter: blur(8px);
  }

  .card-body {
    background: transparent;
    padding: 1.5rem;
  }
`

// Organization Header Card
export const OrganizationHeaderCard = styled(AdminCard)`
  margin-bottom: 2rem;
`

// Stats Cards Container
export const StatsCardsContainer = styled.div<{ $layout?: 'horizontal' | 'vertical' }>`
  display: ${(props) => (props.$layout === 'horizontal' ? 'flex' : 'grid')};
  ${(props) =>
    props.$layout === 'horizontal'
      ? 'gap: 1rem; flex-wrap: wrap;'
      : 'grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem;'}
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    grid-template-columns: 1fr;
  }
`

// Individual Stat Card
export const StatCard = styled(AdminCard)`
  flex: 1;
  min-width: 250px;
  height: 100%;

  .stat-content {
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.15) 100%);
    border: 1px solid rgba(34, 211, 238, 0.25);
    flex-shrink: 0;
  }

  .stat-info {
    flex: 1;

    h6 {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.875rem;
      font-weight: 500;
      margin-bottom: 0.25rem;
    }

    p {
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.75rem;
      margin-bottom: 0.5rem;
    }

    h3 {
      color: #ffffff;
      font-size: 2rem;
      font-weight: 700;
      margin: 0;
      background: linear-gradient(135deg, #ffffff 0%, #22d3ee 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
`

// Admin Account Card
export const AdminAccountCardStyled = styled(AdminCard)`
  height: 100%;

  .card-header {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.22) 0%, rgba(20, 184, 166, 0.15) 100%);
    border-bottom: 1px solid rgba(34, 211, 238, 0.18);
  }

  .admin-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
    border: 3px solid rgba(34, 211, 238, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    box-shadow: 0 8px 24px rgba(34, 211, 238, 0.3);
  }

  .admin-name {
    color: #ffffff;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-align: center;
  }

  .admin-username {
    color: rgba(34, 211, 238, 0.8);
    font-size: 0.875rem;
    text-align: center;
    margin-bottom: 0.75rem;
  }

  .admin-badge {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.15) 100%);
    border: 1px solid rgba(34, 211, 238, 0.3);
    color: #22d3ee;
    padding: 0.375rem 0.75rem;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
    display: inline-block;
    margin: 0 auto;
  }

  .admin-info {
    border-top: 1px solid rgba(34, 211, 238, 0.1);
    padding-top: 1rem;
    margin-top: 1rem;

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }

      .info-label {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.875rem;
      }

      .info-value {
        color: rgba(34, 211, 238, 0.9);
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
  }
`

// Users Table Card
export const UsersTableCard = styled(AdminCard)`
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.1) 100%);
  }

  .table-header-title {
    color: #ffffff;
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
  }
`

// Primary Button
export const PrimaryButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: all 0.25s ease;
  box-shadow: 0 8px 20px rgba(34, 211, 238, 0.35);

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 10px 24px rgba(34, 211, 238, 0.45);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 0.9em;
  }
`

// Danger Button
export const DangerButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: all 0.25s ease;
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.35);

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 10px 24px rgba(239, 68, 68, 0.45);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 0.9em;
  }
`

// Outline Button
export const OutlineButton = styled(ButtonBase)`
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.3);
  color: #22d3ee;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.2);
    border-color: rgba(34, 211, 238, 0.5);
    color: #22d3ee;
    transform: translateY(-1px);
  }
`

// Styled Table
export const StyledTable = styled(Table)`
  color: #ffffff !important;
  margin-bottom: 0;
  background: transparent !important;
  border-collapse: separate;
  border-spacing: 0;

  thead {
    background: linear-gradient(
      135deg,
      rgba(34, 211, 238, 0.12) 0%,
      rgba(20, 184, 166, 0.08) 100%
    ) !important;
    border-bottom: 2px solid rgba(34, 211, 238, 0.25) !important;

    th {
      border-color: rgba(34, 211, 238, 0.2) !important;
      color: rgba(255, 255, 255, 0.95) !important;
      font-weight: 600;
      font-size: 0.875rem;
      padding: 1rem;
      vertical-align: middle;
      background: transparent !important;

      &:hover {
        color: #22d3ee !important;
        cursor: pointer;
        background: rgba(34, 211, 238, 0.08) !important;
      }
    }
  }

  tbody {
    background: transparent !important;

    tr {
      border-color: rgba(34, 211, 238, 0.1) !important;
      background: transparent !important;
      transition: background-color 0.2s ease;

      &:nth-of-type(even) {
        background: rgba(34, 211, 238, 0.02) !important;
      }

      &:hover {
        background-color: rgba(34, 211, 238, 0.08) !important;
      }

      td {
        border-color: rgba(34, 211, 238, 0.1) !important;
        color: rgba(255, 255, 255, 0.9) !important;
        padding: 1rem;
        vertical-align: middle;
        background: transparent !important;
      }
    }
  }

  /* Override Bootstrap table variants */
  &.table-striped > tbody > tr:nth-of-type(odd) > td {
    background-color: transparent !important;
  }

  &.table-hover > tbody > tr:hover > td {
    background-color: rgba(34, 211, 238, 0.08) !important;
  }

  &.table-bordered {
    border-color: rgba(34, 211, 238, 0.18) !important;
  }
`

// Badge Styles
export const StatusBadge = styled.span<{
  $variant: 'success' | 'warning' | 'danger' | 'info' | 'new'
}>`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  ${(props) => {
    switch (props.$variant) {
      case 'success':
        return `
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10b981;
        `
      case 'warning':
        return `
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.15) 100%);
          border: 1px solid rgba(245, 158, 11, 0.3);
          color: #f59e0b;
        `
      case 'danger':
        return `
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(185, 28, 28, 0.15) 100%);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        `
      case 'info':
        return `
          background: linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.15) 100%);
          border: 1px solid rgba(34, 211, 238, 0.3);
          color: #22d3ee;
        `
      case 'new':
        return `
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%);
          border: 1px solid rgba(59, 130, 246, 0.3);
          color: #3b82f6;
        `
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.8);
        `
    }
  }}
`

// Edit Button
export const EditButton = styled.button`
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.25);
  color: rgba(34, 211, 238, 0.9);
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    background: rgba(34, 211, 238, 0.2);
    border-color: rgba(34, 211, 238, 0.4);
    color: #22d3ee;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(34, 211, 238, 0.25);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2);
  }
`

// Header Actions Container
export const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`

// Tab Navigation
export const TabNavContainer = styled.div`
  margin-bottom: 2rem;
  border-bottom: 2px solid rgba(34, 211, 238, 0.18);
  position: relative;
`

export const TabSeparator = styled.div`
  width: 1px;
  height: 32px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(34, 211, 238, 0.3) 20%,
    rgba(34, 211, 238, 0.3) 80%,
    transparent 100%
  );
  margin: 0 0.75rem;
  align-self: center;
`

export const TabNav = styled(Nav)`
  border: none;
  gap: 0.5rem;

  .nav-item {
    margin: 0;
  }

  .nav-link {
    border: none;
    border-radius: 12px 12px 0 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.6);
    padding: 0.875rem 1.5rem;
    font-weight: 500;
    transition: all 0.25s ease;
    position: relative;
    margin-bottom: -2px;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
      background: rgba(34, 211, 238, 0.08);
    }

    &.active {
      color: #22d3ee;
      background: linear-gradient(
        135deg,
        rgba(34, 211, 238, 0.15) 0%,
        rgba(20, 184, 166, 0.1) 100%
      );
      border-bottom: 2px solid #22d3ee;
      font-weight: 600;

      &::before {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent, #22d3ee, transparent);
      }
    }
  }
`

// Tab Content Container
export const TabContentContainer = styled.div`
  padding-top: 2rem;
`
